import { useCallback, useEffect, useRef, useState } from 'react';
const SPEECH_SILENCE_MS = 10000;
const SPEECH_RESTART_MS = 150;
function normalizeSpeech(value) {
    return value
        .toLowerCase()
        .replace(/[.,!?;:'"]+/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
function wordsOf(value) {
    return value.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
}
/** Android often returns the same utterance two or three times in one transcript. */
function collapseFullRepeats(text) {
    const words = wordsOf(text);
    const norms = words.map((word) => normalizeSpeech(word));
    if (norms.length < 2)
        return words.join(' ');
    for (const copies of [3, 2]) {
        if (norms.length % copies !== 0)
            continue;
        const size = norms.length / copies;
        const unit = norms.slice(0, size).join(' ');
        if (!unit)
            continue;
        if (copies === 2 && unit.length < 12)
            continue;
        let same = true;
        for (let copy = 1; copy < copies; copy += 1) {
            if (norms.slice(copy * size, (copy + 1) * size).join(' ') !== unit)
                same = false;
        }
        if (same)
            return words.slice(0, size).join(' ');
    }
    return words.join(' ');
}
/**
 * Chrome on Android resends finalized speech: duplicate entries in one event,
 * the whole phrase repeated inside one string, and the same phrase again after
 * recognition restarts. Keep text that is not already at the end of `already`.
 */
function additionOnly(already, incoming) {
    const nextWords = wordsOf(collapseFullRepeats(incoming));
    const haveWords = wordsOf(already);
    if (nextWords.length === 0)
        return '';
    const nextKey = nextWords.map(normalizeSpeech).join(' ');
    const haveKey = haveWords.map(normalizeSpeech).join(' ');
    if (!nextKey)
        return '';
    if (!haveKey)
        return nextWords.join(' ');
    if (haveKey === nextKey ||
        haveKey.endsWith(` ${nextKey}`) ||
        nextKey === `${haveKey} ${haveKey}` ||
        nextKey === `${haveKey} ${haveKey} ${haveKey}`) {
        return '';
    }
    if (nextKey.startsWith(`${haveKey} `)) {
        const extra = nextWords.slice(haveWords.length);
        return extra.length > 0 ? ` ${extra.join(' ')}` : '';
    }
    return ` ${nextWords.join(' ')}`;
}
function speechFromResults(results) {
    const finals = [];
    let interim = '';
    for (let i = 0; i < results.length; i += 1) {
        const text = results[i][0].transcript.replace(/\s+/g, ' ').trim();
        if (!text)
            continue;
        if (results[i].isFinal) {
            const joined = finals.join(' ');
            const norm = normalizeSpeech(text);
            const joinedNorm = normalizeSpeech(joined);
            if (joinedNorm && norm === joinedNorm)
                continue;
            if (joinedNorm && norm.startsWith(`${joinedNorm} `)) {
                finals.length = 0;
                finals.push(collapseFullRepeats(text));
                continue;
            }
            if (joinedNorm && (joinedNorm === norm || joinedNorm.endsWith(` ${norm}`)))
                continue;
            const prev = finals[finals.length - 1];
            if (prev && normalizeSpeech(prev) === norm)
                continue;
            finals.push(collapseFullRepeats(text));
        }
        else {
            interim = text;
        }
    }
    const finalText = collapseFullRepeats(finals.join(' '));
    const finalNorm = normalizeSpeech(finalText);
    const interimNorm = normalizeSpeech(interim);
    if (interimNorm && (finalNorm === interimNorm || finalNorm.endsWith(` ${interimNorm}`))) {
        interim = '';
    }
    return { finalText, interimText: collapseFullRepeats(interim) };
}
export function useSpeechRecognition(value, onValueChange) {
    const [isListening, setIsListening] = useState(false);
    const [statusText, setStatusText] = useState('');
    const [supported, setSupported] = useState(true);
    const recognitionRef = useRef(null);
    const sessionRef = useRef(null);
    const valueRef = useRef(value);
    valueRef.current = value;
    const clearTimers = useCallback((session) => {
        if (!session)
            return;
        if (session.silenceTimer)
            clearTimeout(session.silenceTimer);
        if (session.restartTimer)
            clearTimeout(session.restartTimer);
        session.silenceTimer = null;
        session.restartTimer = null;
    }, []);
    const finalizeSession = useCallback((updateValue = true) => {
        const session = sessionRef.current;
        if (!session) {
            setIsListening(false);
            setStatusText('');
            return;
        }
        if (updateValue) {
            onValueChange(`${session.baseText}${session.committed}${session.cycleFinal}`.trim());
        }
        clearTimers(session);
        sessionRef.current = null;
        setIsListening(false);
        setStatusText('');
    }, [clearTimers, onValueChange]);
    const resetSilenceTimer = useCallback((session) => {
        if (session.silenceTimer)
            clearTimeout(session.silenceTimer);
        session.silenceTimer = setTimeout(() => {
            session.userActive = false;
            try {
                recognitionRef.current?.stop();
            }
            catch {
                finalizeSession();
            }
        }, SPEECH_SILENCE_MS);
    }, [finalizeSession]);
    const updateTranscript = useCallback((session, interimText = '') => {
        onValueChange(`${session.baseText}${session.committed}${session.cycleFinal}${interimText}`);
    }, [onValueChange]);
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setSupported(false);
            setStatusText('Speech recognition is not supported in this browser. Use Chrome or Edge.');
            return;
        }
        const recognition = new SpeechRecognition();
        const android = /Android/i.test(navigator.userAgent);
        // Chrome on Android repeats phrases when continuous listening stays open.
        recognition.continuous = !android;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;
        recognition.lang = 'en-US';
        recognition.onstart = () => {
            setIsListening(true);
            setStatusText('Listening... you can pause for up to 10 seconds.');
        };
        recognition.onend = () => {
            const session = sessionRef.current;
            if (session) {
                if (session.cycleFinal) {
                    session.committed += session.cycleFinal;
                    session.cycleFinal = '';
                }
            }
            if (session?.userActive) {
                session.restartTimer = setTimeout(() => {
                    if (!sessionRef.current?.userActive)
                        return;
                    try {
                        recognition.start();
                    }
                    catch {
                        session.userActive = false;
                        finalizeSession();
                    }
                }, SPEECH_RESTART_MS);
                return;
            }
            finalizeSession();
        };
        recognition.onerror = (event) => {
            if (event.error === 'aborted')
                return;
            if (event.error === 'no-speech' && sessionRef.current?.userActive) {
                resetSilenceTimer(sessionRef.current);
                return;
            }
            setStatusText(`Speech recognition error: ${event.error}`);
            if (sessionRef.current)
                sessionRef.current.userActive = false;
        };
        recognition.onresult = (event) => {
            const session = sessionRef.current;
            if (!session)
                return;
            resetSilenceTimer(session);
            const { finalText, interimText } = speechFromResults(event.results);
            session.cycleFinal = additionOnly(session.committed, finalText);
            const interim = additionOnly(`${session.committed}${session.cycleFinal}`, interimText);
            updateTranscript(session, interim);
        };
        recognitionRef.current = recognition;
        return () => {
            sessionRef.current = null;
            try {
                recognition.stop();
            }
            catch {
                // ignore
            }
        };
    }, [finalizeSession, resetSilenceTimer, updateTranscript]);
    const startListening = useCallback((initialValue) => {
        const recognition = recognitionRef.current;
        if (!recognition || sessionRef.current?.userActive)
            return;
        const existing = (initialValue ?? valueRef.current).trim();
        const session = {
            userActive: true,
            baseText: existing ? `${existing} ` : '',
            committed: '',
            cycleFinal: '',
            silenceTimer: null,
            restartTimer: null,
        };
        sessionRef.current = session;
        resetSilenceTimer(session);
        try {
            recognition.start();
        }
        catch (error) {
            sessionRef.current = null;
            setStatusText(`Could not start microphone: ${error instanceof Error ? error.message : 'unknown error'}`);
        }
    }, [resetSilenceTimer]);
    const toggleListening = useCallback(() => {
        const recognition = recognitionRef.current;
        if (!recognition)
            return;
        if (isListening) {
            if (sessionRef.current)
                sessionRef.current.userActive = false;
            try {
                recognition.stop();
            }
            catch {
                finalizeSession();
            }
            return;
        }
        startListening();
    }, [finalizeSession, isListening, startListening]);
    const stopListening = useCallback((options) => {
        const session = sessionRef.current;
        const discard = options?.discardTranscript ?? false;
        if (session) {
            session.userActive = false;
            clearTimers(session);
            if (discard) {
                sessionRef.current = null;
                setIsListening(false);
                setStatusText('');
            }
        }
        try {
            recognitionRef.current?.stop();
        }
        catch {
            if (!discard) {
                finalizeSession(true);
            }
        }
    }, [clearTimers, finalizeSession]);
    const discardListening = useCallback(() => {
        stopListening({ discardTranscript: true });
    }, [stopListening]);
    return {
        isListening,
        statusText,
        supported,
        toggleListening,
        startListening,
        stopListening,
        discardListening,
    };
}
